import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { get_orders } from '../../store/reducers/orderReducer';
import { useCurrency } from '../../context/CurrencyContext';
import { ShoppingBag } from 'lucide-react';

const STATUS_COLOR = {
    paid:      'text-green-600',
    pending:   'text-orange-500',
    cancelled: 'text-red-500',
    placed:    'text-blue-500',
    warehouse: 'text-purple-500',
    delivered: 'text-green-600',
    processing:'text-indigo-500',
};

const TABS = [
    { key: 'all',       label: 'All Orders' },
    { key: 'placed',    label: 'Placed' },
    { key: 'pending',   label: 'Pending' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'cancelled', label: 'Cancelled' },
];

const Orders = () => {
    const [status, setStatus] = useState('all');
    const navigate    = useNavigate();
    const dispatch    = useDispatch();
    const { userInfo }   = useSelector(s => s.auth);
    const { myOrders }   = useSelector(s => s.order);
    const { formatBase } = useCurrency();

    useEffect(() => {
        if (userInfo?.id) dispatch(get_orders({ status, customerId: userInfo.id }));
    }, [status, userInfo, dispatch]);

    const getCount = (key) =>
        key === 'all' ? myOrders.length : myOrders.filter(o => o.delivery_status === key).length;

    const redirect = (ord) => {
        let items = 0;
        ord.products.forEach(p => { items += p.quantity; });
        navigate('/payment', { state: { price: ord.price, items, orderId: ord._id } });
    };

    return (
        <div>
            {/* Tabs */}
            <div className="bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden">
                <div className="flex overflow-x-auto border-b border-gray-200">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setStatus(tab.key)}
                            className={`flex items-center gap-1.5 px-5 py-3.5 text-[13px] font-medium whitespace-nowrap border-b-2 -mb-px transition-colors flex-shrink-0 ${
                                status === tab.key
                                    ? 'border-orange-500 text-orange-500'
                                    : 'border-transparent text-gray-600 hover:text-gray-800'
                            }`}
                        >
                            {tab.label}
                            <span
                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                    status === tab.key
                                        ? 'bg-orange-100 text-orange-600'
                                        : 'bg-gray-100 text-gray-500'
                                }`}
                            >
                                {getCount(tab.key)}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Order cards */}
            {myOrders.length > 0 ? (
                myOrders.map(order => {
                    const products   = order.products || [];
                    const statusCls  = STATUS_COLOR[order.delivery_status?.toLowerCase()] || 'text-gray-500';
                    const paymentCls = order.payment_status === 'paid' ? 'text-green-600' : 'text-orange-500';

                    return (
                        <div
                            key={order._id}
                            className="bg-white rounded-lg border border-gray-200 mb-3 overflow-hidden"
                        >
                            {/* Card header */}
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-gray-500">
                                        Order{' '}
                                        <span className="font-mono font-medium text-gray-700">
                                            #{order._id?.slice(-8).toUpperCase()}
                                        </span>
                                    </span>
                                </div>
                                <span className={`text-xs font-semibold capitalize ${statusCls}`}>
                                    {order.delivery_status}
                                </span>
                            </div>

                            {/* Products */}
                            <div className="flex items-center gap-3 px-4 py-4">
                                {/* Thumbnails */}
                                <div className="flex gap-2 flex-shrink-0">
                                    {products.slice(0, 3).map((p, i) => (
                                        <div key={i} className="relative">
                                            <img
                                                src={p.images?.[0]}
                                                alt={p.name}
                                                className="w-[68px] h-[68px] object-cover rounded border border-gray-200"
                                            />
                                            {p.discount > 0 && (
                                                <span className="absolute top-0.5 left-0.5 bg-orange-500 text-white text-[9px] font-bold px-1 rounded">
                                                    -{p.discount}%
                                                </span>
                                            )}
                                        </div>
                                    ))}
                                    {products.length > 3 && (
                                        <div className="w-[68px] h-[68px] rounded border border-gray-200 bg-gray-100 flex items-center justify-center text-xs text-gray-500 font-medium">
                                            +{products.length - 3}
                                        </div>
                                    )}
                                </div>

                                {/* Product info */}
                                <div className="flex-1 min-w-0">
                                    {products[0] && (
                                        <p className="text-sm text-gray-800 leading-snug line-clamp-2">
                                            {products[0].name}
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                        {products.length} item{products.length !== 1 ? 's' : ''}
                                    </p>
                                </div>

                                {/* Price + payment status */}
                                <div className="text-right flex-shrink-0 pl-2">
                                    <p className="text-sm font-bold text-gray-900">
                                        {formatBase(order.price)}
                                    </p>
                                    <p className={`text-[11px] font-medium capitalize mt-0.5 ${paymentCls}`}>
                                        {order.payment_status}
                                    </p>
                                </div>
                            </div>

                            {/* Action buttons */}
                            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
                                <Link
                                    to={`/dashboard/order/details/${order._id}`}
                                    className="px-4 py-1.5 text-xs font-medium border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    View Details
                                </Link>
                                {order.payment_status !== 'paid' && (
                                    <button
                                        onClick={() => redirect(order)}
                                        className="px-4 py-1.5 text-xs font-medium bg-orange-500 hover:bg-orange-600 text-white rounded-full transition-colors"
                                    >
                                        Pay Now
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })
            ) : (
                <div className="bg-white rounded-lg border border-gray-200 flex flex-col items-center py-16">
                    <ShoppingBag className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-sm font-semibold text-gray-600">No orders found</p>
                    <p className="text-xs text-gray-400 mt-1">Orders in this category will appear here</p>
                </div>
            )}
        </div>
    );
};

export default Orders;
